import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Sparkles, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SurveyRespondPage = () => {
  const { shareCode } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const res = await axios.get(`${API}/public/survey/${shareCode}`);
        setSurvey(res.data);
        // Initialize answers
        const initialAnswers = {};
        res.data.questions.forEach(q => {
          if (q.type === 'matrix') {
            initialAnswers[q.id] = {};
          } else if (q.type === 'ranked') {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = '';
          }
        });
        setAnswers(initialAnswers);
      } catch (err) {
        setError(err.response?.data?.detail || 'Survey not found or no longer available');
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [shareCode]);

  const updateAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required questions
    const unanswered = survey.questions.filter(q => {
      if (!q.required) return false;
      const answer = answers[q.id];
      if (q.type === 'matrix') {
        return Object.keys(answer).length < q.matrix_rows.length;
      }
      if (q.type === 'ranked') {
        return answer.length < q.options.length;
      }
      return !answer || answer === '';
    });

    if (unanswered.length > 0) {
      toast.error(`Please answer all required questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/public/survey/${shareCode}/submit`, { answers });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Survey Unavailable</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h1>
          <p className="text-slate-600">Your response has been recorded successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-indigo-600 text-white rounded-t-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6" />
            <span className="font-medium">StatsXY Survey</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">{survey.title}</h1>
          {survey.description && (
            <p className="text-indigo-100">{survey.description}</p>
          )}
        </div>

        {/* Questions */}
        <form onSubmit={handleSubmit} className="bg-white rounded-b-xl shadow-lg">
          {survey.questions.map((question, index) => (
            <div 
              key={question.id} 
              className="p-6 border-b border-slate-200"
              data-testid={`question-${index}`}
            >
              <div className="flex items-start gap-2 mb-4">
                <span className="text-sm font-medium text-slate-500">{index + 1}.</span>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {question.question}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                </div>
              </div>

              {/* Multiple Choice */}
              {question.type === 'multiple_choice' && (
                <div className="space-y-2 ml-6">
                  {question.options?.map((option, optIdx) => (
                    <label key={optIdx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Dropdown */}
              {question.type === 'dropdown' && (
                <div className="ml-6">
                  <select
                    value={answers[question.id]}
                    onChange={(e) => updateAnswer(question.id, e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select an option...</option>
                    {question.options?.map((option, optIdx) => (
                      <option key={optIdx} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Likert Scale */}
              {question.type === 'likert' && (
                <div className="ml-6">
                  <div className="flex justify-between items-center">
                    {Array.from(
                      { length: question.scale_max - question.scale_min + 1 },
                      (_, i) => question.scale_min + i
                    ).map(val => (
                      <label 
                        key={val} 
                        className="flex flex-col items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={val}
                          checked={answers[question.id] === val.toString()}
                          onChange={(e) => updateAnswer(question.id, e.target.value)}
                          className="w-5 h-5 text-indigo-600"
                        />
                        <span className="text-sm font-medium text-slate-700">{val}</span>
                        {question.scale_labels?.[val] && (
                          <span className="text-xs text-slate-500 text-center max-w-[80px]">
                            {question.scale_labels[val]}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Dichotomous (Yes/No) */}
              {question.type === 'dichotomous' && (
                <div className="flex gap-4 ml-6">
                  {['Yes', 'No'].map(option => (
                    <label key={option} className="flex items-center gap-3 p-3 px-6 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Open Ended */}
              {question.type === 'open_ended' && (
                <div className="ml-6">
                  <textarea
                    value={answers[question.id]}
                    onChange={(e) => updateAnswer(question.id, e.target.value)}
                    placeholder="Type your answer here..."
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={4}
                  />
                </div>
              )}

              {/* Demographic - Radio Buttons */}
              {question.type === 'demographic' && question.options && (
                <div className="ml-6 space-y-2">
                  {question.options.map((option, optIdx) => (
                    <label key={optIdx} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name={`demo-${question.id}`}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={() => updateAnswer(question.id, option)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Matrix */}
              {question.type === 'matrix' && (
                <div className="ml-6 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left p-2"></th>
                        {question.matrix_columns?.map((col, colIdx) => (
                          <th key={colIdx} className="text-center p-2 text-sm font-medium text-slate-600">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {question.matrix_rows?.map((row, rowIdx) => (
                        <tr key={rowIdx} className="border-t border-slate-100">
                          <td className="p-2 text-slate-700">{row}</td>
                          {question.matrix_columns?.map((col, colIdx) => (
                            <td key={colIdx} className="text-center p-2">
                              <input
                                type="radio"
                                name={`${question.id}-${rowIdx}`}
                                checked={answers[question.id]?.[row] === col}
                                onChange={() => {
                                  const newMatrix = { ...answers[question.id], [row]: col };
                                  updateAnswer(question.id, newMatrix);
                                }}
                                className="w-4 h-4 text-indigo-600"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Ranked */}
              {question.type === 'ranked' && (
                <div className="ml-6 space-y-2">
                  <p className="text-sm text-slate-500 mb-2">Drag to reorder or click numbers to rank</p>
                  {question.options?.map((option, optIdx) => {
                    const rank = answers[question.id]?.indexOf(option) + 1 || 0;
                    return (
                      <div 
                        key={optIdx}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                        onClick={() => {
                          const current = [...(answers[question.id] || [])];
                          if (current.includes(option)) {
                            // Remove from ranking
                            updateAnswer(question.id, current.filter(o => o !== option));
                          } else {
                            // Add to ranking
                            updateAnswer(question.id, [...current, option]);
                          }
                        }}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          rank > 0 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {rank || '-'}
                        </div>
                        <span className="text-slate-700">{option}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Submit Button */}
          <div className="p-6">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 py-3 text-lg"
              data-testid="submit-survey-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Response'
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-4">
          Powered by StatsXY - AI Research Analytics
        </p>
      </div>
    </div>
  );
};

export default SurveyRespondPage;
